import React, { useState, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { MapPin, MessageCircle, ShoppingBag, Flag, AlertCircle, CheckCircle, Award } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { applyWordFilter } from '../../utils/content';
import { getDisplayLocation } from '../../utils/location';
import Avatar from '../../components/common/Avatar';
import PostMediaGrid from '../../components/common/PostMediaGrid';
import ReportModal from '../../components/moderation/ReportModal';

const UserProfileView = ({ userId }) => {
  const { users, setUsers, posts, currentUser, setCurrentUser, toggleFollow, setView, videoFiles, imageFiles, conversations, setConversations, wordFilters } = useContext(AppContext);
  const user = users.find(u => u.id === userId);
  const userPosts = posts.filter(p => p.userId === userId);
  // Followers are derived from other users' following arrays — `user.followers` is no longer written.
  const actualFollowers = users.filter(u => u.following?.includes(userId)).map(u => u.id);
  const followerCount = actualFollowers.length;
  const [showReportModal, setShowReportModal] = useState(false);
  const [messagingLoading, setMessagingLoading] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  const isBlocked = currentUser.blockedUsers?.includes(userId);

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-black rounded-lg shadow border border-gray-700 p-6 text-center">
          <p className="text-white">User not found</p>
          <button onClick={() => setView('feed')} className="mt-4 px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]">
            Back to Feed
          </button>
        </div>
      </div>
    );
  }

  const isFollowing = currentUser.following?.includes(userId);
  const isOwnProfile = currentUser.id === userId;
  const isMutual = isFollowing && user.following?.includes(currentUser.id);

  const handleMessageUser = async () => {
    if (messagingLoading) return;
    setMessagingLoading(true);
    try {
      // Check local state first
      let existing = conversations.find(c =>
        !c.isGroup &&
        c.participants?.includes(currentUser.id) &&
        c.participants?.includes(userId) &&
        c.participants?.length === 2
      );
      // Also check Firestore directly to avoid duplicates
      if (!existing) {
        const allConvs = await firestoreService.getConversations(currentUser.id);
        existing = allConvs.find(c =>
          !c.isGroup &&
          c.participants?.includes(userId) &&
          c.participants?.length === 2
        );
        if (existing) {
          // Sync into local state
          setConversations(prev => {
            if (prev.find(c => c.id === existing.id)) return prev;
            return [...prev, existing];
          });
        }
      }
      if (existing) {
        setView('messages');
        return;
      }
      // Create new conversation
      const conv = await firestoreService.createConversation({
        participants: [currentUser.id, userId],
        lastMessage: '',
        lastMessageTime: new Date().toISOString(),
      });
      setConversations(prev => [...prev, conv]);
      setView('messages');
    } catch (err) {
      console.error('Error opening conversation:', err);
    } finally {
      setMessagingLoading(false);
    }
  };

  const handleBlockUser = async () => {
    setShowBlockConfirm(false);
    const updatedBlocked = isBlocked
      ? (currentUser.blockedUsers || []).filter(id => id !== userId)
      : [...(currentUser.blockedUsers || []), userId];
    const updatedUser = { ...currentUser, blockedUsers: updatedBlocked };
    // Also unfollow if blocking
    if (!isBlocked && currentUser.following?.includes(userId)) {
      updatedUser.following = updatedUser.following.filter(id => id !== userId);
      firestoreService.toggleFollow(currentUser.id, userId).catch(err => console.error('Error unfollowing blocked user:', err));
    }
    setCurrentUser(updatedUser);
    setUsers(prev => prev.map(u => u.id === currentUser.id ? updatedUser : u));
    try {
      if (isBlocked) {
        await firestoreService.unblockUser(currentUser.id, userId);
      } else {
        await firestoreService.blockUser(currentUser.id, userId);
      }
    } catch (err) {
      console.error('Error blocking/unblocking user:', err);
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Show limited info for banned users (unless viewer is admin/moderator/superadmin)
  if (user.status === 'banned' && currentUser.role !== 'admin' && currentUser.role !== 'superadmin' && currentUser.role !== 'moderator') {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <button
          onClick={() => setView('feed')}
          className="mb-4 text-white hover:text-[#16a34a] flex items-center space-x-2"
        >
          <span>← Back</span>
        </button>

        <div className="bg-black rounded-lg shadow border border-gray-700 p-6 text-center">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-20 h-20 bg-[#16a34a] rounded-full flex items-center justify-center text-pure-white text-2xl font-medium avatar-border">
              {user?.name?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">{user.name}</h2>
              <div className="bg-red-900 border border-red-700 rounded-lg p-4 mt-4">
                <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">Account Banned</p>
                <p className="text-sm text-white mt-2">This account has been permanently banned.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show limited info for suspended users (unless viewer is admin/moderator/superadmin)
  if (user.status === 'suspended' && currentUser.role !== 'admin' && currentUser.role !== 'superadmin' && currentUser.role !== 'moderator') {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <button
          onClick={() => setView('feed')}
          className="mb-4 text-white hover:text-[#16a34a] flex items-center space-x-2"
        >
          <span>← Back</span>
        </button>

        <div className="bg-black rounded-lg shadow border border-gray-700 p-6 text-center">
          <div className="flex flex-col items-center space-y-4">
            <Avatar user={user} size="xl" />
            <div>
              <h2 className="text-2xl font-bold text-white mb-2">{user.name}</h2>
              <div className="bg-yellow-900 border border-yellow-700 rounded-lg p-4 mt-4">
                <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">Account Suspended</p>
                <p className="text-sm text-white mt-2">This account has been temporarily suspended.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button
        onClick={() => setView('feed')}
        className="mb-4 text-white hover:text-white flex items-center space-x-2"
      >
        <span>← Back</span>
      </button>

      <div className="bg-black rounded-lg shadow border border-gray-700 p-6">
        <div className="mb-6">
          <div className="flex items-center space-x-4">
            <Avatar user={user} size="xl" />
            <div>
              <h2 className="text-2xl font-bold text-white">{user.name}</h2>
              <p className="text-sm text-white">@{user.handle}</p>
            </div>
          </div>
          {!isOwnProfile && (
            <div className="flex items-center space-x-2 mt-3">
              <button
                onClick={() => toggleFollow(userId)}
                className={`px-2 py-1 rounded-lg text-sm font-medium ${
                  isFollowing
                    ? 'bg-gray-700 text-white hover:bg-gray-800'
                    : 'bg-[#16a34a] text-pure-white hover:bg-[#15803d]'
                }`}
              >
                {isFollowing ? 'Following' : 'Follow'}
              </button>
              {isMutual && (
                <button
                  onClick={handleMessageUser}
                  className="px-2 py-1 bg-blue-600 text-pure-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center space-x-1"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Message</span>
                </button>
              )}
              <button
                onClick={() => setShowReportModal(true)}
                className="p-1.5 text-white hover:text-red-600 transition-colors"
                title="Report user"
              >
                <Flag className="w-4 h-4" />
              </button>
              <button
                onClick={() => isBlocked ? handleBlockUser() : setShowBlockConfirm(true)}
                className={`px-2 py-1 rounded-lg text-sm font-medium ${
                  isBlocked
                    ? 'bg-gray-700 text-white hover:bg-gray-600'
                    : 'bg-red-600/20 text-red-500 hover:bg-red-600/30'
                }`}
              >
                {isBlocked ? 'Unblock' : 'Block'}
              </button>
            </div>
          )}
          {isOwnProfile && (
            <button 
              onClick={() => setView('profile')}
              className="px-2 py-1 bg-[#16a34a] text-pure-white rounded-lg hover:bg-[#15803d]"
            >
              View My Profile
            </button>
          )}
        </div>

        <div className="space-y-4">
          {user.bio && (
            <div>
              <p className="text-sm font-medium text-white mb-1">Bio</p>
              <p className="text-white">{user.bio}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-white mb-1">Skill Level</p>
              <p className="text-white">{user.skillLevel}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-white mb-1">Handicap</p>
              <p className="text-white">{user.handicap || 'Not set'}</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-white mb-1">Location</p>
            <p className="text-white flex items-center">
              <MapPin className="w-4 h-4 mr-1 text-white" />
              {getDisplayLocation(user.location)}
            </p>
          </div>

          {user.isCoach && user.coachInfo && (
            <div className="bg-gray-900 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Award className="w-5 h-5 text-white" />
                <p className="font-medium text-white">Golf Coach</p>
              </div>
              <p className="text-sm text-white"><strong>Rate:</strong> ${user.coachInfo.rate}/hour</p>
              <p className="text-sm text-white"><strong>Credentials:</strong> {user.coachInfo.credentials}</p>
              {user.coachInfo.verified && (
                <span className="text-xs text-white flex items-center mt-1">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Verified Coach
                </span>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-4 pt-4 border-t mt-6">
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{user.handicap || '-'}</p>
            <p className="text-sm text-white">Handicap</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{followerCount}</p>
            <p className="text-sm text-white">Followers</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-white">{user.following?.length || 0}</p>
            <p className="text-sm text-white">Following</p>
          </div>
        </div>

        <div className="pt-4 border-t mt-4 text-xs text-white">
          <p>Joined {new Date(user.joinDate).toLocaleDateString()}</p>
        </div>
      </div>

      {/* User's Bag */}
      {user.equipment && user.equipment.length > 0 && (
        <div className="mt-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center">
            <ShoppingBag className="w-5 h-5 mr-2" />
            {user.name}'s Bag
          </h2>
          <div className="space-y-3">
            {['driver', 'woods', 'irons', 'wedges', 'putter', 'ball', 'other'].map(cat => {
              const items = user.equipment.filter(eq => eq.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="bg-black rounded-lg border border-gray-700 p-4">
                  <h4 className="text-sm font-bold text-gray-400 mb-2 capitalize">{cat}</h4>
                  <div className="space-y-2">
                    {items.map(eq => (
                      <div key={eq.id} className="bg-gray-900 rounded-lg p-3">
                        <p className="text-white font-medium">{eq.brand} {eq.model}</p>
                        {eq.notes && <p className="text-sm text-gray-400 mt-1">{eq.notes}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* User Posts */}
      <div className="mt-6">
        <h2 className="text-xl font-bold text-white mb-4">Posts ({userPosts.length})</h2>
        {userPosts.length === 0 ? (
          <div className="bg-black rounded-lg shadow border border-gray-700 p-8 text-center">
            <p className="text-white">No posts yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {userPosts.map(post => (
              <div key={post.id} className="bg-black rounded-lg shadow border border-gray-700 p-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-white">
                    {new Date(post.timestamp).toLocaleDateString()}
                    {post.edited && <span className="ml-2 text-white">(edited)</span>}
                  </p>
                  {post.type && (
                    <span className="px-2 py-1 bg-slate-700 text-white text-xs rounded-full">
                      {post.type}
                    </span>
                  )}
                </div>
                <p className="text-white mb-3">{applyWordFilter(post.content, wordFilters)}</p>
                <PostMediaGrid post={post} />
                <div className="flex items-center space-x-4 text-sm text-white">
                  <span>{post.likes?.length || 0} likes</span>
                  <span>{post.comments?.length || 0} comments</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showReportModal && (
        <ReportModal
          onClose={() => setShowReportModal(false)}
          reportType="user"
          targetId={userId}
          targetUserId={userId}
        />
      )}

      {showBlockConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-black rounded-lg p-6 max-w-sm w-full mx-4 border border-gray-700">
            <h3 className="text-lg font-bold text-white mb-2">Block {user.name}?</h3>
            <p className="text-gray-400 mb-6 text-sm">
              They won't be able to see your posts or profile, and you won't see theirs. They won't be notified that you blocked them.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1 px-2 py-1 border border-gray-700 text-white rounded-lg hover:bg-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleBlockUser}
                className="flex-1 px-2 py-1 bg-red-600 text-pure-white rounded-lg hover:bg-red-700"
              >
                Block
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileView;
